import { useState, useCallback, useEffect } from "react";
import CustomModal from "../../Common/CustomModal";
import Select from "react-select";
import chroma from "chroma-js";
import { saveUser, setUserGroups, setUserRoles, getRoles, getGroups } from "../../../api";
import "./AddUserModal.scss";
import { toast } from "react-toastify";
import config from "../../../config";
import { getReactSelectThemeProps } from "../../../helpers/reactSelectThemeStyles";

const identityProvider = config.identityProvider;
const reactSelectThemeProps = getReactSelectThemeProps();

const toOrganizationOption = (organization) => ({
  value: organization?.id ?? null,
  label: organization?.name ?? "",
});

const colourStyles = {
  control: (styles) => ({ ...styles }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.color || "#000000");
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? data.color
        : isFocused
        ? color.alpha(0.1).css()
        : undefined,
      color: isDisabled
        ? "#ccc"
        : isSelected
        ? chroma.contrast(color, "white") > 2
          ? "white"
          : "black"
        : data.color,
      cursor: isDisabled ? "not-allowed" : "default",

      ":active": {
        ...styles[":active"],
        backgroundColor: !isDisabled
          ? isSelected
            ? data.color
            : color.alpha(0.3).css()
          : undefined,
      },
    };
  },
  multiValue: (styles, { data }) => {
    const color = chroma(data.color || "#000000");
    return {
      ...styles,
      backgroundColor: color.alpha(0.1).css(),
    };
  },
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: data.color,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: data.color,
    ":hover": {
      backgroundColor: data.color,
      color: "white",
    },
  }),
};

const AddUserModal = ({ isOpen, organization, organizationsData = [], onClose, onSave }) => {
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [groupsData, setGroupsData] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(() =>
    toOrganizationOption(organization)
  );
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    organization: toOrganizationOption(organization),
  });

  const fetchReferenceData = useCallback(async () => {
    try {
      const [rolesResponse, groupsResponse] = await Promise.all([
        getRoles({ pageSize: 100, page: 1 }),
        getGroups({ pageSize: 1000, page: 1 }),
      ]);

      if (rolesResponse?.results) {
        setRolesData(
          rolesResponse.results.map((role) => ({
            id: role.id,
            value: role.id,
            label: role.name || "",
            color: role.color || "#000000",
            admin_only: role.admin_only,
          }))
        );
      }
      if (groupsResponse?.results) {
        setGroupsData(
          groupsResponse.results.map((group) => ({
            value: group.id,
            label: group.name || "",
            color: group.color || "#000000",
            organization: group.organization,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching reference data", err);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  // Filtering roles for the form
  useEffect(() => {
    // only superadmin can assign admin_only roles
    const roleList = rolesData.filter(
      (role) => role.admin_only !== true || organization?.id === null
    );
    setFilteredRoles(roleList);
  }, [organization, rolesData]);

  // Filtering groups for the form
  useEffect(() => {
    setFilteredGroups(
      groupsData.filter(
        (group) =>
          group.organization?.id === selectedOrganization?.value ||
          (group.organization === null && selectedOrganization?.value === null)
      )
    );
    setSelectedGroups([]);
  }, [selectedOrganization, groupsData]);

  // Update formData when selectedOrganization changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      organization: selectedOrganization ?? toOrganizationOption(organization),
    }));
  }, [selectedOrganization, organization]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const orgOption = toOrganizationOption(organization);
      setFormData({
        username: "",
        email: "",
        password: "",
        organization: orgOption,
      });
      setSelectedRoles([]);
      setSelectedGroups([]);
      setSelectedOrganization(orgOption);
    }
  }, [isOpen, organization]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value ?? "",
    }));
  }, []);

  const handleRoleSelectChange = useCallback((selectedRole) => {
    setSelectedRoles(selectedRole ?? []);
  }, []);

  const handleGroupSelectChange = useCallback((selectedGroup) => {
    setSelectedGroups(selectedGroup ?? []);
  }, []);

  const handleOrganizationSelectChange = useCallback((org) => {
    setSelectedOrganization(org ?? null);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { username, email, password } = formData;
      const roleIds = (selectedRoles ?? []).map((role) => role.id ?? role.value);
      const groupIds = (selectedGroups ?? []).map((group) => group.value);

      try {
        if (identityProvider === "archeo") {
          await saveUser({ username, email, password });
        }

        if (roleIds.length > 0) {
          await setUserRoles({
            username,
            roles: roleIds,
            organizationId: selectedOrganization?.value ?? null,
          });
        }

        // Backend returns 500 for empty groups[]; only call when groups are selected
        if (groupIds.length > 0) {
          await setUserGroups({
            username,
            groups: groupIds,
          });
        }

        toast.success("User saved successfully");
      } catch (err) {
        console.error("Error while saving user", err);
        toast.error("Error while saving user");
      } finally {
        onSave();
      }
    },
    [formData, selectedRoles, selectedGroups, selectedOrganization, onSave]
  );

  return (
    <CustomModal
      title={"Add New User"}
      isVisible={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <form onSubmit={handleSubmit}>
        <div className="add-user-modal-body">
          <div className="input-wrapper">
            <label htmlFor="username" className="input-label">
              Username:
            </label>
            <input
              id="username"
              type="text"
              name="username"
              value={formData.username ?? ""}
              onChange={handleChange}
            />
          </div>

          {identityProvider === "archeo" ? (
            <div className="input-wrapper">
              <label htmlFor="email" className="input-label">
                Email:
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email ?? ""}
                onChange={handleChange}
              />
            </div>
          ) : null}

          {organization?.id === null ? (
            <div className="input-wrapper">
              <label htmlFor="organization" className="input-label">
                Organization:
              </label>
              <Select
                name="organization"
                options={organizationsData ?? []}
                value={selectedOrganization ?? null}
                placeholder="Seleziona un'organizzazione"
                onChange={handleOrganizationSelectChange}
                isClearable
                {...reactSelectThemeProps}
              />
            </div>
          ) : null}

          {identityProvider === "archeo" ? (
            <div className="input-wrapper">
              <label htmlFor="password" className="input-label">
                Password:
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password ?? ""}
                onChange={handleChange}
              />
            </div>
          ) : null}

          <div className="input-wrapper">
            <label htmlFor="roles" className="input-label">
              Roles:
            </label>
            <Select
              name="roles"
              isMulti
              value={selectedRoles ?? []}
              options={filteredRoles ?? []}
              placeholder="Seleziona un ruolo"
              onChange={handleRoleSelectChange}
              {...reactSelectThemeProps}
            />
          </div>

          <div className="input-wrapper">
            <label htmlFor="groups" className="input-label">
              Groups:
            </label>
            <Select
              isMulti
              value={selectedGroups ?? []}
              name="groups"
              options={filteredGroups ?? []}
              placeholder="Seleziona un gruppo"
              classNamePrefix={reactSelectThemeProps.classNamePrefix}
              styles={{
                ...reactSelectThemeProps.styles,
                ...colourStyles,
              }}
              onChange={handleGroupSelectChange}
            />
          </div>
        </div>

        <footer className="modal-footer">
          <button type="submit" className="modal-footer-button">
            Aggiungi
          </button>
        </footer>
      </form>
    </CustomModal>
  );
};

export default AddUserModal;
